import { defineStore } from 'pinia';
import type GuestInfo from '../types/GuestInfo'
import CateringOrdersService from '../services/CateringOrders';
import OrdersService from '../services/Orders';
import { useFlashMessageStore } from './flashMessages';
import type FlashMessage from '../types/FlashMessage';
import type Location from '../classes/Location';
import type AvailableTime from '../types/AvailableTime';
import type Product from '../classes/Product';
import type CateringOrder from '../types/CateringOrder';
import type CateringOrderSuggestion from '../classes/CateringOrderSuggestion';
import type ChoiceCustomFieldInterface from '@/types/ChoiceCustomField';
import type OrderProduct from '../types/OrderProduct';
import { useLocationsStore } from './locations';
import { plainToClass } from 'class-transformer';
import { DateTime } from 'luxon';

export const useCateringOrderStore = defineStore({
  id: "cateringOrderStore",
  state: () => ({
    selectedTimeWanted: null as (Date | null),
    selectedHandOffMode: null as (string | null),
    cateringOrder: null as (CateringOrder | null),
    assistanceLevel: 0,
    preAdjustmentTime:null as (DateTime | null),
    suggestions: null as (CateringOrderSuggestion[] | null)
  }),
  actions: {
    setSelectedHandOffMode(mode: string) {
      this.selectedHandOffMode = mode;
    },
    setAssistanceLevel(num: number) {
      this.assistanceLevel = num;
      if (this.cateringOrder) {
        this.cateringOrder.assistance_level = num;
      }
    },
    fetchCateringOrder(id: number): Promise<any> {
      return new Promise((resolve, reject) => {
        CateringOrdersService.getCateringOrder(id).then((order: CateringOrder) => {
          // Update the time_wanted to be user's timezone
          let now = DateTime.fromJSDate(new Date);
          let localOffset = now.zone;
          let dt = DateTime.fromISO(order.time_wanted, {setZone: true})
          dt = dt.setZone(localOffset, {keepLocalTime: true})

          order.time_wanted = dt.toISO() as string;
          this.cateringOrder = order

          this.assistanceLevel = order.assistance_level;

          resolve(order);
        }).catch((error: Error) => {
          // Delete the localstorage key
          localStorage.removeItem('cateringOrder');
          reject(error);
        });
      })
    },
    clearCateringOrder() {
      this.cateringOrder = null as (CateringOrder | null);
      this.selectedTimeWanted = null as (Date | null);
      this.selectedHandOffMode = null as (string | null);
      this.preAdjustmentTime = null as (DateTime | null);
      this.suggestions = null as (CateringOrderSuggestion[] | null);
    },
    createCateringOrder(order: CateringOrder): Promise<any> {
      return new Promise((resolve, reject) => {
        CateringOrdersService.createCateringOrder(order).then((savedOrder: CateringOrder) => {
          localStorage.setItem('cateringOrder', savedOrder.id.toString());
          this.cateringOrder = savedOrder;
          this.suggestions = null; // The order was created, so make sure we can get updated suggestions
          resolve(savedOrder)
        }).catch((error: Error) => {
          reject(error);
        });
      });
    },
    updateCateringOrder(id: number, order: CateringOrder): Promise<any> {
      return new Promise((resolve, reject) => {
        CateringOrdersService.updateCateringOrder(id, order).then((savedOrder: CateringOrder) => {
          localStorage.setItem('cateringOrder', savedOrder.id.toString());
          
          if (this.cateringOrder) {
            let locationNotEqual = order.store != this.cateringOrder.store;
            let guestNotEqual = order.guest_count != this.cateringOrder.guest_count;
            let hungerNotEqual = (order.light_eaters != this.cateringOrder.light_eaters || order.regular_eaters != this.cateringOrder.regular_eaters || order.hungry_eaters != this.cateringOrder.hungry_eaters); 
            let eventTypeNotEqual = order.event_type != this.cateringOrder.event_type;
            if (guestNotEqual || hungerNotEqual || eventTypeNotEqual || locationNotEqual) {
              // The order was updated to affect suggestions, so make sure we can get updated suggestions
              this.fetchCateringOrderSuggestion()
            }
          }

          this.cateringOrder = savedOrder;

          resolve(savedOrder)
        }).catch((error: Error) => {
          reject(error);
        });
      });
    },
    fetchCateringOrderSuggestion(): Promise<any> {
      const store = this;
      return new Promise((resolve, reject) => {
        if (store.cateringOrder && store.cateringOrder.id) {
          CateringOrdersService.getCateringOrderSuggestion(store.cateringOrder.id).then((orderSuggestion: CateringOrderSuggestion[]) => {
            this.suggestions = orderSuggestion;
            resolve(orderSuggestion);
          }).catch((error: Error) => {
            this.suggestions = [] as CateringOrderSuggestion[];
            reject(error);
          });
        } else {
          this.suggestions = [] as CateringOrderSuggestion[];
          reject('');
        }
      })
    },
    trackSuggestionConversion(suggestion_id: string, product: OrderProduct): Promise<any> {
      return new Promise((resolve, reject) => {
        if (this.cateringOrder && this.cateringOrder.id) {
          CateringOrdersService.trackSuggestionConversion(this.cateringOrder.id, suggestion_id, product).then(() => {
            resolve('');
          }).catch((error: Error) => {
            reject(error)
          });
        }
      });
    },
  }
})
